import * as Sentry from '@sentry/vue'
import { AxiosError } from 'axios'
import type { AxiosResponse } from 'axios'

export type ExpectedErrors = Map<
  number,
  string | ((backendError: string) => string)
>

export type ExpectedAxiosError = {
  status: 'expectedError'
  error: string
  statusCode?: number
}

export type UnexpectedAxiosError = {
  status: 'unexpectedError'
  error: string
  statusCode?: number
}

type SuccessAxiosResponse<TData> = {
  status: 'success'
  data: TData
  headers: AxiosResponse['headers']
}

export type SafeAxiosResponse<Data> =
  | SuccessAxiosResponse<Data>
  | ExpectedAxiosError
  | UnexpectedAxiosError

export type StoryRailsError =
  | string
  | string[]
  | { error?: string }
  | { '': string[] }
  | { errors?: Record<string, string[]> }

const defaultErrorMessage = 'Unexpected error occurred, please try again later'

const getApiErrorMessage = (error: StoryRailsError | undefined): string => {
  if (!error) {
    return defaultErrorMessage
  }
  if (typeof error === 'string') {
    return error
  }
  if (Array.isArray(error)) {
    return error[0] ?? defaultErrorMessage
  }
  if ('error' in error) {
    return error.error ?? defaultErrorMessage
  }
  if ('errors' in error) {
    const errors = error.errors
    if (!errors) {
      return defaultErrorMessage
    }
    // Get the first error from the first field
    const firstField = Object.keys(errors)[0]
    if (firstField && errors[firstField] && errors[firstField].length > 0) {
      return errors[firstField][0] ?? defaultErrorMessage
    }
    return defaultErrorMessage
  }
  if ('' in error && Array.isArray(error[''])) {
    return error[''][0] ?? defaultErrorMessage
  }

  return defaultErrorMessage
}
/**
/**
 * Handles an API request and returns a standardized response object.
 *
 * This function executes the provided `apiCall` function, which should return a promise
 * resolving to an Axios response. It supports mapping specific HTTP status codes to
 * custom error messages via the `expectedErrors` map. If an error occurs and its status
 * code is found in `expectedErrors`, an `ExpectedAxiosError` is returned. Otherwise, the error
 * is reported to Sentry and an `UnexpectedAxiosError` is returned.
 *
 * @param {Object} params - Parameters for the request handler.
 * @param {() => Promise<any>} params.apiCall - A function that returns a Promise for the API call, ie. `() => client.get('/api/endpoint')`
 * @param {Map<number, string | ((backendError: string) => string)>} params.expectedErrors - A map of HTTP status codes to user-facing error messages, the value of the Map must a string or a function (receiving the API error) that returns a string, ie. new Map([[401, 'Unauthorized'], [404, 'Not Found'], [422, (backendError) => backendError]])
 * @returns A promise resolving to a `SafeAxiosResponse<TData>` object, which can be a success, an expected error, or an unexpected error.
 */
export async function handleRequest<TData>({
  apiCall,
  expectedErrors,
}: {
  apiCall: () => Promise<AxiosResponse<TData>>
  expectedErrors?: ExpectedErrors
}): Promise<SafeAxiosResponse<TData>> {
  try {
    const response = await apiCall()
    return {
      status: 'success',
      data: response.data,
      headers: response.headers,
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      if (
        expectedErrors &&
        error.response?.status &&
        expectedErrors.has(error.response.status)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        let errorMessage = expectedErrors.get(error.response.status)!
        if (typeof errorMessage === 'function') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          errorMessage = errorMessage(getApiErrorMessage(error.response?.data))
        }

        return {
          status: 'expectedError',
          error: errorMessage,
          statusCode: error.response.status,
        }
      }

      Sentry.captureException(error)

      return {
        status: 'unexpectedError',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        error: getApiErrorMessage(error.response?.data),
        statusCode: error.response?.status,
      }
    }

    Sentry.captureException(error)
    return {
      status: 'unexpectedError',
      error: defaultErrorMessage,
    }
  }
}

type ExpectedOpenApiError<TError> = {
  status: 'expectedError'
  error: string
  backendError: TError // exposes the raw backend error through the openAPI schema
  response: Response
  statusCode: number // for convenience
}

type UnexpectedOpenApiError = {
  status: 'unexpectedError'
  error: string
  response: Response
}

type SuccessOpenApiResponse<TData> = {
  status: 'success'
  data: TData
  response: Response
  error?: never
}

type SafeOpenApiResponse<TData, TError> =
  | SuccessOpenApiResponse<TData>
  | ExpectedOpenApiError<TError>
  | UnexpectedOpenApiError

// This type is just to make sure the openapi-fetch response is typed correctly and we are not breaking its type safety
// We are still letting openapi-fetch handle the type inference
type OpenApiResponse =
  | {
      data: unknown
      error?: never
      response: Response
    }
  | {
      data?: never
      error: StoryRailsError
      response: Response
    }

export const handleRequestOpenApi = async <TResponse extends OpenApiResponse>({
  apiCall,
  expectedErrors,
}: {
  apiCall: () => Promise<TResponse>
  expectedErrors?: ExpectedErrors
}): Promise<
  SafeOpenApiResponse<NonNullable<TResponse['data']>, TResponse['error']>
> => {
  try {
    const { data, error, response } = await apiCall()
    if (data) {
      return {
        status: 'success',
        data,
        response,
      }
    }

    if (expectedErrors && expectedErrors.has(response.status)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      let errorMessage = expectedErrors.get(response.status)!
      if (typeof errorMessage === 'function') {
        errorMessage = errorMessage(getApiErrorMessage(error))
      }
      return {
        status: 'expectedError',
        error: errorMessage,
        backendError: error,
        response,
        statusCode: response.status,
      }
    }

    Sentry.captureException(error)
    return {
      status: 'unexpectedError',
      error: getApiErrorMessage(error),
      response,
    }
  } catch (error) {
    Sentry.captureException(error)
    return {
      status: 'unexpectedError',
      error: defaultErrorMessage,
      response: new Response('Network error occurred, please try again later', {
        status: 500,
      }),
    }
  }
}
